import { useNavigate } from 'react-router-dom';
import type { Startup } from '@shared/types';
import type { Pipeline } from '../../store/pipelineStore';

const STEP_NAMES = ['Profile', 'Idea', 'Reality Check', 'Positioning', 'Validation'];

interface Props {
  startup: Startup;
  startupId: string;
  pipeline: Pipeline | null;
  doneCount: number;
  betaUserCount: number;
  feedbackCount: number;
  validationTasksDone?: number;
  validationTasksTotal?: number;
  founderSummaryDone?: boolean;
  hasFinancials?: boolean;
  onRunIntelligence: () => void;
  onScrollTo: (key: string) => void;
}

export function NextMoveCard({
  startup, startupId, pipeline, doneCount, betaUserCount, feedbackCount,
  validationTasksDone = 0, validationTasksTotal = 14, founderSummaryDone = false, hasFinancials = false,
  onRunIntelligence, onScrollTo,
}: Props) {
  const navigate = useNavigate();

  const pipelineStepsDone = pipeline ? [
    pipeline.userProfile?.completedAt,
    pipeline.idea?.completedAt,
    pipeline.realityCheck?.completedAt,
    pipeline.positioning?.completedAt,
    pipeline.validationPath?.completedAt,
  ].filter(Boolean).length : 0;

  const pipelineTotal = pipeline ? 5 : 0;
  const totalItems = pipelineTotal + 5 + 1 + 1;
  const completedItems = pipelineStepsDone + doneCount + (betaUserCount > 0 ? 1 : 0) + (feedbackCount > 0 ? 1 : 0);
  const overallProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Priority logic — first match wins
  let icon = '';
  let cta = '';
  let sub = '';
  let onAction: () => void;

  if (!pipeline) {
    icon = '';
    cta = 'Start your validation pipeline';
    sub = 'Validate your idea before building — it takes just 20 minutes.';
    onAction = () => navigate('/pipeline');
  } else if (pipeline.status !== 'completed') {
    icon = '';
    const stepName = STEP_NAMES[pipeline.currentStep] ?? 'Next Step';
    cta = `Continue pipeline: ${stepName}`;
    sub = "You're building momentum — complete your validation pipeline.";
    onAction = () => navigate(`/pipeline/${pipeline.id}`);
  } else if (doneCount === 0) {
    icon = '';
    cta = 'Run Intelligence Analysis';
    sub = 'Analyze your name, keywords, competitors, trends, and market sentiment.';
    onAction = onRunIntelligence;
  } else if (doneCount < 5) {
    icon = '';
    cta = `Finish Intelligence Analysis (${doneCount}/5 done)`;
    sub = `More data = sharper decisions. ${5 - doneCount} section${5 - doneCount > 1 ? 's' : ''} remaining.`;
    onAction = () => onScrollTo('nameValidation');
  } else if (validationTasksDone === 0) {
    icon = '';
    cta = 'Start your 14-day validation plan';
    sub = 'Execute your personalized validation plan to test your idea with real users.';
    onAction = () => onScrollTo('validationPlan');
  } else if (validationTasksDone < validationTasksTotal) {
    icon = '';
    cta = `Continue validation plan (${validationTasksDone}/${validationTasksTotal} done)`;
    sub = `Keep going — complete your remaining ${validationTasksTotal - validationTasksDone} tasks.`;
    onAction = () => onScrollTo('validationPlan');
  } else if (betaUserCount === 0) {
    icon = '';
    cta = 'Add your first beta tester';
    sub = 'Real user feedback is more valuable than any analysis.';
    onAction = () => navigate(`/startups/${startupId}/beta`);
  } else if (!founderSummaryDone) {
    icon = '';
    cta = "Write your Founder's Summary";
    sub = 'Summarize your vision to complete your readiness profile.';
    onAction = () => onScrollTo('feedback');
  } else if (!hasFinancials) {
    icon = '';
    cta = 'Set up your financial model';
    sub = 'Model revenue scenarios and find your break-even month.';
    onAction = () => navigate(`/startups/${startupId}/financials`);
  } else if (feedbackCount === 0) {
    icon = '';
    cta = 'Collect feedback from beta testers';
    sub = 'Ask your beta users what they really think.';
    onAction = () => navigate(`/startups/${startupId}/feedback`);
  } else {
    icon = '';
    cta = `${startup.name} is well-instrumented!`;
    sub = 'Keep iterating — review your key insights and plan next milestones.';
    onAction = () => onScrollTo('overview');
  }

  const progressColor = overallProgress >= 80
    ? 'linear-gradient(90deg, #22c55e, #7c6ff7)'
    : 'linear-gradient(90deg, #7c6ff7, #00d4ff)';

  return (
    <div style={{
      background: 'rgba(124, 111, 247, 0.06)',
      border: '1px solid rgba(124, 111, 247, 0.25)',
      borderLeft: '4px solid #7c6ff7',
      borderRadius: 12,
      padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 200 }}>
          <span style={{ fontSize: 22, lineHeight: 1.3, marginTop: 1, flexShrink: 0 }}>{icon}</span>
          <div>
            <p style={{ fontSize: 11, color: '#7c6ff7', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Your Next Move
            </p>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 3 }}>{cta}</p>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>{sub}</p>
          </div>
        </div>
        <button
          onClick={onAction}
          className="btn-primary"
          style={{ fontSize: 13, padding: '8px 18px', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center' }}
        >
          Let's go →
        </button>
      </div>

      {/* Overall progress bar */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            Overall progress · {completedItems}/{totalItems} items
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700,
            color: overallProgress >= 80 ? '#22c55e' : overallProgress >= 40 ? '#f59e0b' : '#9ca3af',
          }}>
            {overallProgress}%
          </span>
        </div>
        <div style={{ height: 5, background: '#1e1e30', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${overallProgress}%`,
            background: progressColor, borderRadius: 3, transition: 'width 0.8s ease',
          }} />
        </div>
      </div>
    </div>
  );
}
